/**
 * Signed uploads (block 06). Reused unchanged by block 12's screenshots and
 * block 25's WhatsApp media — the endpoint takes no caller-specific flag.
 */
import { signUploadResultSchema, storageUsageSchema, } from "@nook/schema";
export function signUpload(http, body) {
    return http.request({
        path: "/v1/uploads/sign",
        method: "POST",
        body,
        schema: signUploadResultSchema,
    });
}
export function getStorageUsage(http) {
    return http.request({ path: "/v1/uploads/usage", schema: storageUsageSchema });
}
/**
 * PUT the bytes at the signed URL.
 *
 * Deliberately **not** through `http.ts`: this request carries no bearer token,
 * no surface header and no Nook error envelope — it goes to Supabase, not to
 * us. Teaching the shared client about a second contract to save one `fetch`
 * would be the exact coupling that file exists to avoid.
 *
 * `XMLHttpRequest` rather than `fetch`, because `fetch` emits no upload
 * progress and a 20MB file on a slow connection is the case a progress bar is
 * for. XHR is present in browsers and in React Native.
 *
 * **It is not present in an MV3 service worker**, which is where block 12
 * uploads from — `chrome.tabs.captureVisibleTab` exists nowhere else, and the
 * popup is closed long before the bytes are. So there is a `fetch` path for
 * that caller. It is a fallback rather than a second exported function on
 * purpose: a screenshot nobody sized has no progress bar to feed, and giving
 * the extension its own upload contract is the coupling this file avoids.
 */
export function putSignedUpload(url, file, onProgress) {
    if (typeof XMLHttpRequest === "undefined")
        return putWithFetch(url, file, onProgress);
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url);
        xhr.setRequestHeader("content-type", file.type);
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                onProgress?.(Math.round((event.loaded / event.total) * 100));
            }
        };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error("Upload failed. Check your connection."));
        xhr.send(file);
    });
}
async function putWithFetch(url, file, onProgress) {
    const response = await fetch(url, {
        method: "PUT",
        headers: { "content-type": file.type },
        body: file,
    }).catch(() => {
        // A `TypeError` here is the network, not the request. Same sentence as the
        // XHR path so the two transports are indistinguishable to a caller.
        throw new Error("Upload failed. Check your connection.");
    });
    if (!response.ok)
        throw new Error(`Upload failed (${response.status})`);
    onProgress?.(100);
}
//# sourceMappingURL=uploads.js.map